/**
 * confirm-dialog.ts - Custom confirmation dialog utility
 *
 * Provides a custom styled confirmation modal as an alternative to the native confirm() dialog.
 */

import { t } from './i18n-helper';

/**
 * Show a confirmation dialog with a custom message
 * @param message - The confirmation message to display
 * @returns Promise that resolves to true if confirmed, false if cancelled
 */
export function showConfirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.style.display = 'block';

    const cancelText = t('common.cancel', 'Cancel');
    const deleteText = t('common.delete', 'Delete');

    // Create modal content
    modal.innerHTML = `
      <div class="modal-content confirm-modal-content">
        <div class="confirm-modal-message">${escapeHtml(message)}</div>
        <div class="confirm-modal-actions">
          <button class="btn btn-secondary" id="confirm-cancel">${escapeHtml(cancelText)}</button>
          <button class="btn btn-danger" id="confirm-delete">${escapeHtml(deleteText)}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Handle confirm
    const confirmBtn = modal.querySelector('#confirm-delete');
    const cancelBtn = modal.querySelector('#confirm-cancel');

    const cleanup = () => {
      document.body.removeChild(modal);
    };

    confirmBtn?.addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    cancelBtn?.addEventListener('click', () => {
      cleanup();
      resolve(false);
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup();
        resolve(false);
      }
    });

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        document.removeEventListener('keydown', handleEscape);
        resolve(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
  });
}

/**
 * Show a warning confirmation dialog
 * @param message - The warning message to display
 * @param confirmText - Text for the confirm button (default: "Continue")
 * @param cancelText - Text for the cancel button (default: "Cancel")
 * @returns Promise that resolves to true if confirmed, false if cancelled
 */
export function showWarningConfirmDialog(
  message: string,
  confirmText: string = 'Continue',
  cancelText: string = 'Cancel'
): Promise<boolean> {
  return new Promise((resolve) => {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.style.display = 'block';

    // Create modal content
    modal.innerHTML = `
      <div class="modal-content confirm-modal-content">
        <div class="confirm-modal-message" style="white-space: pre-wrap;">${escapeHtml(message)}</div>
        <div class="confirm-modal-actions">
          <button class="btn btn-secondary" id="confirm-cancel">${escapeHtml(cancelText)}</button>
          <button class="btn btn-warning" id="confirm-continue" style="background-color: #ff9800; border-color: #ff9800; color: white;">${escapeHtml(confirmText)}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Handle confirm
    const confirmBtn = modal.querySelector('#confirm-continue');
    const cancelBtn = modal.querySelector('#confirm-cancel');

    const cleanup = () => {
      document.body.removeChild(modal);
    };

    confirmBtn?.addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    cancelBtn?.addEventListener('click', () => {
      cleanup();
      resolve(false);
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup();
        resolve(false);
      }
    });

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        document.removeEventListener('keydown', handleEscape);
        resolve(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
  });
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
